import { Box, Skeleton, Stack, Typography, tokens } from '@cast/design-system';

const NAV_WIDTHS = ['52%', '38%', '60%', '44%', '56%', '34%'];

const ROW_WIDTHS = ['40%', '32%', '46%', '30%', '38%', '42%'];

const getOrgPreviewInitials = (name: string) => {
  const trimmed = name.trim();

  if (!trimmed) {
    return 'YO';
  }

  return trimmed.slice(0, 2).toUpperCase();
};

const getOrgPreviewName = (name: string) => {
  const trimmed = name.trim();

  return trimmed || 'Your org';
};

const CastLogoMark = () => (
  <svg viewBox="0 0 275 300" fill="none" aria-hidden="true">
    <path
      d="M263.758 72.63L208.538 24.775C189.484 8.26 165.576 0 141.675 0C117.775 0 93.8718 8.2575 74.8077 24.7775L22.1653 70.4C8.07984 82.6075 0 100.318 0 118.988V181.012C0 199.682 8.07985 217.392 22.1678 229.602L74.8102 275.223C93.2435 291.2 116.989 300 141.675 300C166.361 300 190.107 291.203 208.54 275.225L263.76 227.37C273.363 219.045 277.225 205.715 273.598 193.405C271.88 187.577 268.308 182.188 263.267 177.82L248.65 165.152L224.917 185.723L242.46 200.95C243.016 201.432 243.335 202.132 243.335 202.87C243.335 203.607 243.016 204.31 242.458 204.792L188.297 251.73C175.009 263.245 158.339 269.005 141.675 269.005C125.007 269.005 108.344 263.247 95.0535 251.73L42.4086 206.107C35.134 199.803 30.9607 190.652 30.9607 181.01V118.985C30.9607 109.34 35.134 100.192 42.4086 93.8875L95.0535 48.2625C107.91 37.1225 124.466 30.9875 141.675 30.9875C158.884 30.9875 175.443 37.1225 188.297 48.265L242.532 95.2675C243.088 95.75 243.41 96.45 243.41 97.19C243.41 97.93 243.091 98.63 242.532 99.1125L141.675 186.518L99.5359 149.997L141.673 113.478L166.306 134.857L190 114.325L159.844 88.1925C149.486 79.215 133.862 79.215 123.504 88.1925L75.5182 129.78C69.6547 134.86 66.2916 142.227 66.2916 150C66.2916 157.773 69.6547 165.14 75.5158 170.217L123.501 211.805C133.862 220.785 149.486 220.787 159.844 211.805L263.267 122.173C268.308 117.805 271.88 112.415 273.595 106.588C277.22 94.2825 273.358 80.9525 263.758 72.6275V72.63Z"
      fill="currentColor"
    />
  </svg>
);

export const BrowserImage = ({ orgName }: { orgName: string }) => {
  const previewName = getOrgPreviewName(orgName);
  const previewInitials = getOrgPreviewInitials(orgName);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflow: 'hidden',
        bgcolor: tokens.theme.bg.structural.surfaceSubtle,
        borderLeft: `1px solid ${tokens.theme.border.structural.neutral}`,
        pt: '64px',
        px: '64px',
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          maxWidth: 860,
          bgcolor: tokens.theme.bg.structural.surface,
          border: `1px solid ${tokens.theme.border.structural.neutral}`,
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          boxShadow: '0 10px 32px rgba(8, 16, 40, 0.06)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Browser chrome */}
        <Stack
          direction="row"
          alignItems="center"
          gap="12px"
          sx={{
            flexShrink: 0,
            height: 38,
            px: '14px',
            borderBottom: `1px solid ${tokens.theme.border.structural.neutral}`,
            bgcolor: tokens.theme.bg.structural.surfaceSubtle,
          }}
        >
          {/* Window control dots */}
          <Stack direction="row" gap="7px">
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: '#ec6a5e',
              }}
            />
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: '#f4bf4f',
              }}
            />
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: '#61c554',
              }}
            />
          </Stack>

          {/* URL bar */}
          <Box
            sx={{
              flex: 1,
              maxWidth: 480,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              px: '12px',
              borderRadius: 999,
              bgcolor: tokens.theme.bg.structural.surface,
              border: `1px solid ${tokens.theme.border.structural.neutral}`,
            }}
          >
            <Typography
              variant="P12R"
              sx={{
                color: tokens.theme.fg.structural.secondary,
                fontFamily: 'monospace',
              }}
            >
              app.cast.ai
            </Typography>
          </Box>
        </Stack>

        {/* Browser body */}
        <Stack direction="row" sx={{ flex: 1, minHeight: 0 }}>
          {/* Sidebar */}
          <Stack
            sx={{
              flexShrink: 0,
              width: 216,
              borderRight: `1px solid ${tokens.theme.border.structural.neutral}`,
              p: '16px 12px',
              gap: '14px',
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                p: '4px 6px 10px',
                color: tokens.theme.fg.structural.primary,
                '& svg': { height: 16, width: 'auto', display: 'block' },
              }}
            >
              <CastLogoMark />
            </Box>

            {/* Org card */}
            <Stack
              direction="row"
              alignItems="center"
              gap="10px"
              sx={{
                p: '10px',
                borderRadius: '8px',
                bgcolor: tokens.theme.bg.selection.primarySubtle,
                border: `1px solid color-mix(in srgb, ${tokens.theme.bg.action.primary} 30%, transparent)`,
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  borderRadius: '4px',
                  bgcolor: tokens.theme.bg.action.primary,
                  color: tokens.theme.fg.action.onPrimary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                }}
              >
                {previewInitials}
              </Box>
              <Typography
                variant="P14B"
                noWrap
                sx={{ color: tokens.theme.fg.structural.primary, minWidth: 0 }}
              >
                {previewName}
              </Typography>
            </Stack>

            {/* Nav skeleton items */}
            <Stack gap="14px" sx={{ px: '8px', pt: '6px' }}>
              {NAV_WIDTHS.map((width, index) => (
                <Skeleton
                  key={index}
                  variant="rounded"
                  animation={false}
                  height={8}
                  sx={{ width, borderRadius: 4 }}
                />
              ))}
            </Stack>
          </Stack>

          {/* Main content area */}
          <Stack sx={{ flex: 1, p: '20px', gap: '12px', minWidth: 0 }}>
            {/* Title skeleton */}
            <Skeleton
              variant="rounded"
              animation={false}
              height={12}
              sx={{ width: 160, borderRadius: 6, mb: '8px' }}
            />

            {/* Table rows */}
            {ROW_WIDTHS.map((width, index) => (
              <Stack
                key={index}
                direction="row"
                alignItems="center"
                gap="14px"
                sx={{
                  height: 44,
                  px: '14px',
                  border: `1px solid ${tokens.theme.border.structural.neutral}`,
                  borderRadius: '8px',
                }}
              >
                <Skeleton
                  variant="circular"
                  animation={false}
                  width={12}
                  height={12}
                  sx={{ flexShrink: 0 }}
                />
                <Skeleton
                  variant="rounded"
                  animation={false}
                  height={8}
                  sx={{ width, borderRadius: 4 }}
                />
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};
